import React, { useState } from 'react';
import DataTable, { Pagination } from '../components/common/DataTable';
import StatusBadge from '../components/common/StatusBadge';
import { useFetch } from '../hooks/useFetch';
import { merchant as merchantApi } from '../services/api';
import { formatSats, formatUSD, formatDate, truncateTxId, explorerLink, formatPercent } from '../utils/format';

export default function Chargebacks() {
  const [page, setPage] = useState(1);

  const { data, loading, error, refetch } = useFetch(
    () => merchantApi.chargebacks({ page, limit: 20 }),
    [page]
  );
  const { data: stats } = useFetch(merchantApi.stats);

  const chargebacks = data?.chargebacks || [];

  const totalClawedBack = chargebacks
    .filter(cb => cb.status === 'completed')
    .reduce((sum, cb) => sum + cb.amount_sats, 0);

  const columns = [
    {
      key: 'created_at',
      label: 'Date',
      sortable: true,
      render: (val) => (
        <span className="text-gray-400 whitespace-nowrap text-xs">{formatDate(val)}</span>
      ),
    },
    {
      key: 'stripe_dispute_id',
      label: 'Dispute ID',
      render: (val) => (
        <span className="font-mono text-xs text-gray-400" title={val}>
          {val || '—'}
        </span>
      ),
    },
    {
      key: 'amount_cents',
      label: 'Amount (USD)',
      sortable: true,
      render: (val) => <span className="font-medium text-gray-200">{formatUSD(val)}</span>,
    },
    {
      key: 'amount_sats',
      label: 'Amount (sats)',
      sortable: true,
      render: (val) => <span className="font-mono text-gray-300">{formatSats(val)}</span>,
    },
    {
      key: 'status',
      label: 'Status',
      render: (val) => <StatusBadge status={val} />,
    },
    {
      key: 'tx_id',
      label: 'Clawback TX',
      render: (val) =>
        val ? (
          <a
            href={explorerLink(val)}
            target="_blank"
            rel="noopener noreferrer"
            className="font-mono text-xs text-blue-400 hover:text-blue-300 transition-colors"
            title={val}
          >
            {truncateTxId(val)}
          </a>
        ) : (
          <span className="text-gray-600 text-xs">—</span>
        ),
    },
  ];

  return (
    <div className="p-8 max-w-6xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-white">Chargebacks</h1>
        <p className="text-sm text-gray-400 mt-1">Stripe disputes and escrow clawback history</p>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-3 gap-4 mb-8">
        <div className="card">
          <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Total Chargebacks</div>
          <div className="text-2xl font-bold text-red-400">{data?.total ?? '—'}</div>
          <div className="text-xs text-gray-500 mt-1">all time</div>
        </div>
        <div className="card">
          <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Total Clawed Back</div>
          <div className="text-2xl font-bold text-gray-100">{formatSats(totalClawedBack)}</div>
          <div className="text-xs text-gray-500 mt-1">from escrow</div>
        </div>
        <div className="card">
          <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Chargeback Rate</div>
          <div className={`text-2xl font-bold ${(stats?.chargeback_rate || 0) > 1 ? 'text-red-400' : 'text-green-400'}`}>
            {formatPercent(stats?.chargeback_rate)}
          </div>
          <div className="text-xs text-gray-500 mt-1">of all payments</div>
        </div>
      </div>

      <div className="card p-0 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-800">
          <h2 className="text-base font-semibold text-white">Chargeback History</h2>
        </div>

        <DataTable
          columns={columns}
          data={chargebacks}
          loading={loading}
          error={error}
          onRetry={refetch}
          emptyMessage="No chargebacks yet — great news!"
        />

        <Pagination
          page={page}
          pages={data?.pages || 1}
          onPageChange={setPage}
        />
      </div>
    </div>
  );
}
